'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckIcon, CopyIcon } from 'lucide-react';
import type { ComponentProps, CSSProperties, HTMLAttributes } from 'react';
import { createContext, memo, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BundledLanguage, BundledTheme, HighlighterGeneric, ThemedToken } from 'shiki';
import { createHighlighter } from 'shiki';
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

// Shiki uses bitflags for font styles: 1=italic, 2=bold, 4=underline
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check

const isItalic = (fontStyle: number | undefined) => fontStyle && fontStyle & 1;
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check

const isBold = (fontStyle: number | undefined) => fontStyle && fontStyle & 2;
const isUnderline = (fontStyle: number | undefined) =>
// biome-ignore lint/suspicious/noBitwiseOperators: shiki bitflag check
fontStyle && fontStyle & 4;

// Transform tokens to include pre-computed keys to avoid noArrayIndexKey lint
type KeyedToken = {
  token: ThemedToken;
  key: string;
};
type KeyedLine = {
  tokens: KeyedToken[];
  key: string;
};
type CodeBlockLanguage = BundledLanguage | 'text';
const addKeysToTokens = (lines: ThemedToken[][]): KeyedLine[] => lines.map((line, lineIdx) => ({
  key: `line-${lineIdx}`,
  tokens: line.map((token, tokenIdx) => ({
    key: `line-${lineIdx}-${tokenIdx}`,
    token
  }))
}));

// Line number styles using CSS counters
const LINE_NUMBER_CLASSES = cn('block', 'before:content-[counter(line)]', 'before:inline-block', 'before:[counter-increment:line]', 'before:w-8', 'before:mr-4', 'before:text-right', 'before:text-muted-foreground/50', 'before:font-mono', 'before:select-none');
const TOKEN_THEME_CLASSES = cn('text-[var(--shiki-light)]', 'dark:text-[var(--shiki-dark)]', 'bg-[var(--shiki-light-bg)]', 'dark:bg-[var(--shiki-dark-bg)]', '[font-weight:var(--shiki-light-font-weight)]', 'dark:[font-weight:var(--shiki-dark-font-weight)]', '[font-style:var(--shiki-light-font-style)]', 'dark:[font-style:var(--shiki-dark-font-style)]', '[text-decoration:var(--shiki-light-text-decoration)]', 'dark:[text-decoration:var(--shiki-dark-text-decoration)]');
const getThemedStyleValue = (styles: Record<string, string> | undefined, property: string): string | undefined => {
  const value = styles?.[property];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};
const getFirstThemedStyleValue = (styles: Record<string, string> | undefined, properties: string[]): string | undefined => {
  for (const property of properties) {
    const value = getThemedStyleValue(styles, property);
    if (value) {
      return value;
    }
  }
  return undefined;
};
const buildTokenThemeStyle = (token: ThemedToken): CSSProperties => {
  const htmlStyle = token.htmlStyle as Record<string, string> | undefined;
  const fallbackWeight = isBold(token.fontStyle) ? 'bold' : 'inherit';
  const fallbackFontStyle = isItalic(token.fontStyle) ? 'italic' : 'inherit';
  const fallbackTextDecoration = isUnderline(token.fontStyle) ? 'underline' : 'none';
  const lightColor = getFirstThemedStyleValue(htmlStyle, ['color']) ?? token.color ?? 'inherit';
  const lightBackground = getFirstThemedStyleValue(htmlStyle, ['--shiki-light-bg', 'backgroundColor', 'background-color']) ?? token.bgColor ?? 'transparent';
  return {
    '--shiki-light': lightColor,
    '--shiki-dark': getFirstThemedStyleValue(htmlStyle, ['--shiki-dark']) ?? lightColor,
    '--shiki-light-bg': lightBackground,
    '--shiki-dark-bg': getFirstThemedStyleValue(htmlStyle, ['--shiki-dark-bg']) ?? lightBackground,
    '--shiki-light-font-weight': getFirstThemedStyleValue(htmlStyle, ['--shiki-light-font-weight', 'fontWeight', 'font-weight']) ?? fallbackWeight,
    '--shiki-dark-font-weight': getFirstThemedStyleValue(htmlStyle, ['--shiki-dark-font-weight', '--shiki-light-font-weight', 'fontWeight', 'font-weight']) ?? fallbackWeight,
    '--shiki-light-font-style': getFirstThemedStyleValue(htmlStyle, ['--shiki-light-font-style', 'fontStyle', 'font-style']) ?? fallbackFontStyle,
    '--shiki-dark-font-style': getFirstThemedStyleValue(htmlStyle, ['--shiki-dark-font-style', '--shiki-light-font-style', 'fontStyle', 'font-style']) ?? fallbackFontStyle,
    '--shiki-light-text-decoration': getFirstThemedStyleValue(htmlStyle, ['--shiki-light-text-decoration', 'textDecoration', 'text-decoration']) ?? fallbackTextDecoration,
    '--shiki-dark-text-decoration': getFirstThemedStyleValue(htmlStyle, ['--shiki-dark-text-decoration', '--shiki-light-text-decoration', 'textDecoration', 'text-decoration']) ?? fallbackTextDecoration
  } as CSSProperties;
};

// Token rendering component
const TokenSpan = t0 => {
  const $ = _c(5);
  const {
    token
  } = t0;
  let t1;
  if ($[0] !== token) {
    t1 = buildTokenThemeStyle(token);
    $[0] = token;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== token.content) {
    t2 = <span className={TOKEN_THEME_CLASSES} style={t1}>{token.content}</span>;
    $[2] = t1;
    $[3] = token.content;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
type HighlightTone = 'info' | 'success';
const HIGHLIGHT_CLASSES = {
  info: '-mx-4 px-4 bg-orange-500/15 shadow-[inset_3px_0_0_var(--color-orange-400)]',
  success: '-mx-4 px-4 bg-green-500/20 shadow-[inset_3px_0_0_#22c55e]'
};

// Line rendering component
const LineSpan = t0 => {
  const $ = _c(11);
  const {
    keyedLine,
    showLineNumbers,
    isHighlighted,
    isHighlightStart,
    isHighlightEnd,
    highlightTone
  } = t0;
  const t1 = showLineNumbers ? LINE_NUMBER_CLASSES : "block";
  const t2 = isHighlighted && HIGHLIGHT_CLASSES[highlightTone];
  const t3 = isHighlighted && highlightTone === "info" && isHighlightStart && "pt-1";
  const t4 = isHighlighted && highlightTone === "info" && isHighlightEnd && "pb-1";
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = cn(t1, t2, t3, t4);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = isHighlighted || undefined;
  let t7;
  if ($[5] !== keyedLine.tokens) {
    t7 = keyedLine.tokens.length === 0 ? "\n" : keyedLine.tokens.map(_temp);
    $[5] = keyedLine.tokens;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t5 || $[8] !== t6 || $[9] !== t7) {
    t8 = <span className={t5} data-highlighted={t6}>{t7}</span>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
};

// Types
type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: CodeBlockLanguage;
  showLineNumbers?: boolean;
  highlightedLines?: number[];
  highlightTone?: HighlightTone;
};
type TokenizedCode = {
  tokens: ThemedToken[][];
  fg: string;
  bg: string;
};
type CodeBlockContextType = {
  code: string;
};

// Context
const CodeBlockContext = createContext<CodeBlockContextType>({
  code: ''
});

// Highlighter cache (singleton per language)
const highlighterCache = new Map<string, Promise<HighlighterGeneric<BundledLanguage, BundledTheme>>>();

// Token cache
const tokensCache = new Map<string, TokenizedCode>();

// Subscribers for async token updates
const subscribers = new Map<string, Set<(result: TokenizedCode) => void>>();
const getTokensCacheKey = (code: string, language: CodeBlockLanguage) => {
  return JSON.stringify([language, code]);
};
const getHighlighter = (language: BundledLanguage): Promise<HighlighterGeneric<BundledLanguage, BundledTheme>> => {
  const cached = highlighterCache.get(language);
  if (cached) {
    return cached;
  }
  const highlighterPromise = createHighlighter({
    langs: [language],
    themes: ['github-light', 'github-dark'],
    engine: createJavaScriptRegexEngine()
  });
  highlighterCache.set(language, highlighterPromise);
  return highlighterPromise;
};

// Create raw tokens for immediate display while highlighting loads
const createRawTokens = (code: string): TokenizedCode => ({
  bg: 'transparent',
  fg: 'inherit',
  tokens: code.split('\n').map(line => line === '' ? [] : [{
    color: 'inherit',
    content: line
  } as ThemedToken])
});

// Synchronous highlight with callback for async results
export const highlightCode = (code: string, language: CodeBlockLanguage, callback?: (result: TokenizedCode) => void): TokenizedCode | null => {
  if (language === 'text') {
    return createRawTokens(code);
  }
  const tokensCacheKey = getTokensCacheKey(code, language);

  // Return cached result if available
  const cached = tokensCache.get(tokensCacheKey);
  if (cached) {
    return cached;
  }

  // Subscribe callback if provided
  if (callback) {
    if (!subscribers.has(tokensCacheKey)) {
      subscribers.set(tokensCacheKey, new Set());
    }
    subscribers.get(tokensCacheKey)?.add(callback);
  }

  // Start highlighting in background - fire-and-forget async pattern
  getHighlighter(language).then(highlighter => {
    const availableLangs = highlighter.getLoadedLanguages();
    const langToUse = availableLangs.includes(language) ? language : 'md';
    const result = highlighter.codeToTokens(code, {
      lang: langToUse,
      themes: {
        dark: 'github-dark',
        light: 'github-light'
      }
    });
    const tokenized: TokenizedCode = {
      bg: result.bg ?? 'transparent',
      fg: result.fg ?? 'inherit',
      tokens: result.tokens
    };

    // Cache the result
    tokensCache.set(tokensCacheKey, tokenized);

    // Notify all subscribers
    const subs = subscribers.get(tokensCacheKey);
    if (subs) {
      for (const sub of subs) {
        sub(tokenized);
      }
      subscribers.delete(tokensCacheKey);
    }
  }).catch(error => {
    console.error('Failed to highlight code:', error);
    subscribers.delete(tokensCacheKey);
  });
  return null;
};
const CodeBlockBody = memo(t0 => {
  const $ = _c(25);
  const {
    tokenized,
    showLineNumbers,
    highlightedLines,
    highlightTone: t1,
    className
  } = t0;
  const highlightTone = t1 === undefined ? "info" : t1;
  let style;
  if ($[0] !== tokenized.bg || $[1] !== tokenized.fg) {
    style = {};
    for (const raw of [tokenized.bg, tokenized.fg]) {
      for (const part of raw.split(";")) {
        const trimmed = part.trim();
        if (!trimmed) {
          continue;
        }
        if (trimmed.startsWith("--")) {
          const colonIdx = trimmed.indexOf(":");
          if (colonIdx !== -1) {
            style[trimmed.slice(0, colonIdx)] = trimmed.slice(colonIdx + 1);
          }
        } else {
          if (raw === tokenized.bg) {
            style.backgroundColor = trimmed;
          } else {
            style.color = trimmed;
          }
        }
      }
    }
    $[0] = tokenized.bg;
    $[1] = tokenized.fg;
    $[2] = style;
  } else {
    style = $[2];
  }
  const preStyle = style;
  let t2;
  if ($[3] !== tokenized.tokens) {
    t2 = addKeysToTokens(tokenized.tokens);
    $[3] = tokenized.tokens;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const keyedLines = t2;
  let t3;
  if ($[5] !== className) {
    t3 = cn("m-0 box-border w-max min-w-full p-4 text-sm dark:bg-(--shiki-dark-bg)! dark:text-(--shiki-dark)!", className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = showLineNumbers && "[counter-increment:line_0] [counter-reset:line]";
  let t5;
  if ($[7] !== t4) {
    t5 = cn("font-mono text-sm", t4);
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== highlightTone || $[10] !== highlightedLines || $[11] !== keyedLines || $[12] !== showLineNumbers) {
    let t7;
    if ($[14] !== highlightTone || $[15] !== highlightedLines || $[16] !== showLineNumbers) {
      t7 = (keyedLine, index) => <LineSpan key={keyedLine.key} keyedLine={keyedLine} showLineNumbers={showLineNumbers} isHighlighted={highlightedLines?.includes(index + 1) ?? false} isHighlightStart={!highlightedLines?.includes(index)} isHighlightEnd={!highlightedLines?.includes(index + 2)} highlightTone={highlightTone} />;
      $[14] = highlightTone;
      $[15] = highlightedLines;
      $[16] = showLineNumbers;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t6 = keyedLines.map(t7);
    $[9] = highlightTone;
    $[10] = highlightedLines;
    $[11] = keyedLines;
    $[12] = showLineNumbers;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <code className={t5}>{t6}</code>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== preStyle || $[22] !== t3 || $[23] !== t7) {
    t8 = <pre className={t3} style={preStyle}>{t7}</pre>;
    $[21] = preStyle;
    $[22] = t3;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}, (prevProps, nextProps) => prevProps.tokenized === nextProps.tokenized && prevProps.showLineNumbers === nextProps.showLineNumbers && prevProps.highlightedLines === nextProps.highlightedLines && prevProps.highlightTone === nextProps.highlightTone && prevProps.className === nextProps.className);
CodeBlockBody.displayName = 'CodeBlockBody';
export const CodeBlockContainer = t0 => {
  const $ = _c(14);
  let className;
  let language;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      language,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = language;
    $[3] = props;
    $[4] = style;
  } else {
    className = $[1];
    language = $[2];
    props = $[3];
    style = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("group relative box-border min-w-0 w-full max-w-full overflow-hidden rounded-md border bg-background text-foreground", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== style) {
    t2 = {
      containIntrinsicSize: "auto 200px",
      contentVisibility: "auto",
      ...style
    };
    $[7] = style;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== language || $[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t3 = <div className={t1} data-language={language} style={t2} {...props} />;
    $[9] = language;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
export const CodeBlockHeader = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex items-center justify-between border-b bg-muted/80 px-3 py-2 text-muted-foreground text-xs", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockTitle = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex items-center gap-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockFilename = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("font-mono", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <span className={t1} {...props}>{children}</span>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockActions = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("-my-1 -mr-1 flex items-center gap-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const CodeBlockContent = t0 => {
  const $ = _c(16);
  const {
    code,
    language,
    showLineNumbers: t1,
    highlightedLines,
    highlightTone: t2
  } = t0;
  const showLineNumbers = t1 === undefined ? false : t1;
  const highlightTone = t2 === undefined ? "info" : t2;
  let t3;
  if ($[0] !== code) {
    t3 = createRawTokens(code);
    $[0] = code;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const rawTokens = t3;
  let t4;
  if ($[2] !== code || $[3] !== language || $[4] !== rawTokens) {
    t4 = () => highlightCode(code, language) ?? rawTokens;
    $[2] = code;
    $[3] = language;
    $[4] = rawTokens;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [tokenized, setTokenized] = useState(t4);
  let t5;
  let t6;
  if ($[6] !== code || $[7] !== language || $[8] !== rawTokens) {
    t5 = () => {
      let cancelled = false;
      setTokenized(highlightCode(code, language, result => {
        if (!cancelled) {
          setTokenized(result);
        }
      }) ?? rawTokens);
      return () => {
        cancelled = true;
      };
    };
    t6 = [code, language, rawTokens];
    $[6] = code;
    $[7] = language;
    $[8] = rawTokens;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== highlightTone || $[12] !== highlightedLines || $[13] !== showLineNumbers || $[14] !== tokenized) {
    t7 = <div className="relative min-w-0 max-w-full overflow-auto"><CodeBlockBody showLineNumbers={showLineNumbers} tokenized={tokenized} highlightedLines={highlightedLines} highlightTone={highlightTone} /></div>;
    $[11] = highlightTone;
    $[12] = highlightedLines;
    $[13] = showLineNumbers;
    $[14] = tokenized;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export const CodeBlock = t0 => {
  const $ = _c(26);
  let children;
  let className;
  let code;
  let highlightedLines;
  let language;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      code,
      language,
      showLineNumbers: t1,
      highlightedLines,
      highlightTone: t2,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = code;
    $[4] = highlightedLines;
    $[5] = language;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    code = $[3];
    highlightedLines = $[4];
    language = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const showLineNumbers = t1 === undefined ? false : t1;
  const highlightTone = t2 === undefined ? "info" : t2;
  let t3;
  if ($[9] !== code) {
    t3 = {
      code
    };
    $[9] = code;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const contextValue = t3;
  let t4;
  if ($[11] !== code || $[12] !== highlightTone || $[13] !== highlightedLines || $[14] !== language || $[15] !== showLineNumbers) {
    t4 = <CodeBlockContent code={code} language={language} showLineNumbers={showLineNumbers} highlightedLines={highlightedLines} highlightTone={highlightTone} />;
    $[11] = code;
    $[12] = highlightTone;
    $[13] = highlightedLines;
    $[14] = language;
    $[15] = showLineNumbers;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== children || $[18] !== className || $[19] !== language || $[20] !== props || $[21] !== t4) {
    t5 = <CodeBlockContainer className={className} language={language} {...props}>{children}{t4}</CodeBlockContainer>;
    $[17] = children;
    $[18] = className;
    $[19] = language;
    $[20] = props;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== contextValue || $[24] !== t5) {
    t6 = <CodeBlockContext value={contextValue}>{t5}</CodeBlockContext>;
    $[23] = contextValue;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
};
export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};
export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<number>(0);
  const {
    code
  } = use(CodeBlockContext);
  const copyToClipboard = useCallback(async () => {
    if (typeof window === 'undefined' || !navigator?.clipboard?.writeText) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }
    try {
      if (!isCopied) {
        await navigator.clipboard.writeText(code);
        setIsCopied(true);
        onCopy?.();
        timeoutRef.current = window.setTimeout(() => setIsCopied(false), timeout);
      }
    } catch (error) {
      onError?.(error as Error);
    }
  }, [code, onCopy, onError, timeout, isCopied]);
  useEffect(() => () => {
    window.clearTimeout(timeoutRef.current);
  }, []);
  const Icon = isCopied ? CheckIcon : CopyIcon;
  return <Button className={cn('size-7 shrink-0', className)} onClick={copyToClipboard} size="icon" variant="ghost" {...props}>
      {children ?? <Icon size={14} />}
    </Button>;
};
export type CodeBlockLanguageSelectorProps = ComponentProps<typeof Select>;
export const CodeBlockLanguageSelector = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Select {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type CodeBlockLanguageSelectorTriggerProps = ComponentProps<typeof SelectTrigger>;
export const CodeBlockLanguageSelectorTrigger = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("h-7 border-none bg-transparent px-2 text-xs shadow-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectTrigger className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type CodeBlockLanguageSelectorValueProps = ComponentProps<typeof SelectValue>;
export const CodeBlockLanguageSelectorValue = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SelectValue {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type CodeBlockLanguageSelectorContentProps = ComponentProps<typeof SelectContent>;
export const CodeBlockLanguageSelectorContent = t0 => {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      align: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const align = t1 === undefined ? "end" : t1;
  let t2;
  if ($[3] !== align || $[4] !== props) {
    t2 = <SelectContent align={align} {...props} />;
    $[3] = align;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export type CodeBlockLanguageSelectorItemProps = ComponentProps<typeof SelectItem>;
export const CodeBlockLanguageSelectorItem = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SelectItem {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(t0) {
  const {
    token,
    key
  } = t0;
  return <TokenSpan key={key} token={token} />;
}